import moment from 'moment';
import { AGUARDANDO_DIGITACAO, EM_DIGITACAO } from '../../../../constants/status';
import {
  formatMedicaoPeriodLabel,
  getMedicaoPeriod,
  normalizeFilterForApi,
} from '../shared';

export { getMedicaoPeriod, formatMedicaoPeriodLabel, normalizeFilterForApi };

export const PERIODO_PRESET_KEYS = {
  ULTIMOS_7_DIAS: 'ULTIMOS_7_DIAS',
  ULTIMOS_15_DIAS: 'ULTIMOS_15_DIAS',
  ULTIMOS_30_DIAS: 'ULTIMOS_30_DIAS',
  ULTIMA_MEDICAO: 'ULTIMA_MEDICAO',
  MEDICAO_ATUAL: 'MEDICAO_ATUAL',
};

/**
 * Medição atual (painel digitador): ciclo 26→25 — reutiliza getMedicaoPeriod.
 * Ex.: 04/08 → 26/07–25/08 · 26/08 → 26/08–25/09.
 */
export const getMedicaoAtualPeriod = (ref = moment()) => getMedicaoPeriod(ref);

/** Ciclo imediatamente anterior à medição atual (mesmo deslocamento −1 mês). */
export const getUltimaMedicaoPeriod = (ref = moment()) => {
  const atual = getMedicaoAtualPeriod(ref);
  return {
    inicio: moment(atual.inicio).subtract(1, 'month').valueOf(),
    fim: moment(atual.fim).subtract(1, 'month').valueOf(),
  };
};

export const PERIODO_PRESETS = [
  { id: PERIODO_PRESET_KEYS.ULTIMOS_7_DIAS, label: 'Últimos 7 dias' },
  { id: PERIODO_PRESET_KEYS.ULTIMOS_15_DIAS, label: 'Últimos 15 dias' },
  { id: PERIODO_PRESET_KEYS.ULTIMOS_30_DIAS, label: '30 dias' },
  { id: PERIODO_PRESET_KEYS.ULTIMA_MEDICAO, label: 'Última medição' },
  { id: PERIODO_PRESET_KEYS.MEDICAO_ATUAL, label: 'Medição atual' },
];

export const getPeriodoPresetRange = (presetId, ref = moment()) => {
  const now = moment(ref);
  switch (presetId) {
    case PERIODO_PRESET_KEYS.ULTIMOS_7_DIAS:
      return {
        inicio: now.clone().subtract(6, 'days').startOf('day').valueOf(),
        fim: now.clone().endOf('day').valueOf(),
      };
    case PERIODO_PRESET_KEYS.ULTIMOS_15_DIAS:
      return {
        inicio: now.clone().subtract(14, 'days').startOf('day').valueOf(),
        fim: now.clone().endOf('day').valueOf(),
      };
    case PERIODO_PRESET_KEYS.ULTIMOS_30_DIAS:
      return {
        inicio: now.clone().subtract(29, 'days').startOf('day').valueOf(),
        fim: now.clone().endOf('day').valueOf(),
      };
    case PERIODO_PRESET_KEYS.ULTIMA_MEDICAO:
      return getUltimaMedicaoPeriod(ref);
    case PERIODO_PRESET_KEYS.MEDICAO_ATUAL:
    default:
      return getMedicaoAtualPeriod(ref);
  }
};

export const matchPeriodoPreset = (inicio, fim) => {
  if (inicio == null || fim == null) return null;
  const match = PERIODO_PRESETS.find((preset) => {
    const range = getPeriodoPresetRange(preset.id);
    return range.inicio === inicio && range.fim === fim;
  });
  return match?.id ?? null;
};

export const KPI_KEYS = {
  DIGITADOS: 'digitados',
  MEDIA: 'media',
  FILA: 'fila',
  PENDENTES: 'pendentes',
  ERROS: 'erros',
  DIAS_UTEIS: 'dias_uteis',
};

export const FILA_SORT_MODES = {
  STATUS: 'STATUS',
  VENCIMENTO_ASC: 'VENCIMENTO_ASC',
  VENCIMENTO_DESC: 'VENCIMENTO_DESC',
  CODIGO_ASC: 'CODIGO_ASC',
  CODIGO_DESC: 'CODIGO_DESC',
};

export const EMPTY_MINHA_FILA = {
  nsirsAtivas: 0,
  nsirsAtribuidas: 0,
  servicosPendentesNaFila: 0,
  itens: [],
};

export const EMPTY_OPERADOR_RESUMO = {
  servicosDigitados: 0,
  mediaDiaUtil: 0,
  errosIntegracao: 0,
  meuPeriodo: {
    metaPeriodo: null,
    tendencia7dias: [],
    comparacaoMedia: 'na_media',
  },
};

export const getDefaultFilterDigitador = (authData) => {
  const { inicio, fim } = getMedicaoAtualPeriod();
  return {
    inicio,
    fim,
    dateTypes: ['DATA_DIGITACAO'],
    digitadorId: authData?.id ? [authData.id] : [],
  };
};

/** Normaliza resposta de POST /reports/digitacao/me/resumo (métricas pessoais do período). */
export const normalizeOperadorResumo = (data) => {
  if (!data || typeof data !== 'object') {
    return { ...EMPTY_OPERADOR_RESUMO };
  }

  return {
    servicosDigitados: data.servicosDigitados ?? data.meuPeriodo?.servicosDigitados ?? 0,
    mediaDiaUtil: data.mediaDiaUtil ?? data.meuPeriodo?.mediaDiaUtil ?? 0,
    errosIntegracao: data.errosIntegracao ?? 0,
    meuPeriodo: {
      metaPeriodo: data.meuPeriodo?.metaPeriodo ?? null,
      tendencia7dias: data.meuPeriodo?.tendencia7dias ?? [],
      comparacaoMedia: data.meuPeriodo?.comparacaoMedia ?? 'na_media',
    },
  };
};

const countPendentesFila = (itens) =>
  (itens || []).reduce((acc, item) => {
    const total = item.totalServicos ?? item.total ?? 0;
    const digitados = item.digitados ?? 0;
    return acc + Math.max(0, total - digitados);
  }, 0);

/** Converte vencimento (DD/MM/YYYY ou epoch) para moment endOf('day'). Null se inválido. */
export const parseFilaVencimentoMoment = (vencimento) => {
  if (vencimento == null || vencimento === '') return null;
  if (typeof vencimento === 'number') {
    const parsed = moment(vencimento);
    return parsed.isValid() ? parsed.endOf('day') : null;
  }
  const parsed = moment(vencimento, ['DD/MM/YYYY', moment.ISO_8601], true);
  return parsed.isValid() ? parsed.endOf('day') : null;
};

/** Converte vencimento (DD/MM/YYYY ou epoch) para ordenação ASC. */
export const parseVencimentoForSort = (vencimento) => {
  const parsed = parseFilaVencimentoMoment(vencimento);
  return parsed ? parsed.valueOf() : Infinity;
};

const compareNsirCodigo = (a, b) =>
  String(a.nsirCodigo ?? '').localeCompare(String(b.nsirCodigo ?? ''), undefined, { numeric: true });

const statusSortRank = (status) => {
  if (status === EM_DIGITACAO) return 0;
  if (status === AGUARDANDO_DIGITACAO) return 1;
  return 2;
};

/** Ordena itens da fila. Default: STATUS → vencimento ASC → código ASC. */
export const sortFilaItens = (itens, mode = FILA_SORT_MODES.STATUS) => {
  const list = [...(itens || [])];

  if (mode === FILA_SORT_MODES.VENCIMENTO_ASC) {
    return list.sort((a, b) => {
      const byVenc = parseVencimentoForSort(a.vencimento) - parseVencimentoForSort(b.vencimento);
      return byVenc !== 0 ? byVenc : compareNsirCodigo(a, b);
    });
  }

  if (mode === FILA_SORT_MODES.VENCIMENTO_DESC) {
    return list.sort((a, b) => {
      const va = parseVencimentoForSort(a.vencimento);
      const vb = parseVencimentoForSort(b.vencimento);
      const byVenc = (vb === Infinity ? -Infinity : vb) - (va === Infinity ? -Infinity : va);
      if (va === Infinity && vb === Infinity) return compareNsirCodigo(a, b);
      if (va === Infinity) return 1;
      if (vb === Infinity) return -1;
      return byVenc !== 0 ? byVenc : compareNsirCodigo(a, b);
    });
  }

  if (mode === FILA_SORT_MODES.CODIGO_ASC) {
    return list.sort(compareNsirCodigo);
  }

  if (mode === FILA_SORT_MODES.CODIGO_DESC) {
    return list.sort((a, b) => compareNsirCodigo(b, a));
  }

  // STATUS (padrão)
  return list.sort((a, b) => {
    const byStatus = statusSortRank(a.status) - statusSortRank(b.status);
    if (byStatus !== 0) return byStatus;
    const byVenc = parseVencimentoForSort(a.vencimento) - parseVencimentoForSort(b.vencimento);
    if (byVenc !== 0) return byVenc;
    return compareNsirCodigo(a, b);
  });
};

/** @deprecated use sortFilaItens */
export const sortFilaItensPorVencimento = (itens) =>
  sortFilaItens(itens, FILA_SORT_MODES.VENCIMENTO_ASC);

/** KPIs da zona Operação. Pendentes = fila inteira; NSIRs em digitação = só EM_DIGITACAO. */
export const aggregateFilaMetrics = (itens, filaMeta = {}) => {
  const all = itens || [];
  const emDigitacao = all.filter((item) => item.status === EM_DIGITACAO);

  const nsirsAtribuidas =
    filaMeta.nsirsAtribuidas ?? filaMeta.nsirsAtivas ?? all.length;

  const servicosTotal = all.reduce(
    (acc, item) => acc + (item.totalServicos ?? item.total ?? 0),
    0,
  );

  const servicosPendentes =
    filaMeta.servicosPendentesNaFila ?? countPendentesFila(all);

  return {
    nsirsEmDigitacao: emDigitacao.length,
    nsirsAtribuidas,
    servicosPendentes,
    servicosTotal,
  };
};

/** Normaliza resposta de POST /reports/digitacao/me/fila (sem sort — view ordena). */
export const normalizeMinhaFilaResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return { ...EMPTY_MINHA_FILA };
  }

  if (Array.isArray(data)) {
    const itens = data;
    const nsirsAtribuidas = itens.length;
    const servicosPendentesNaFila = countPendentesFila(itens);
    return {
      nsirsAtivas: itens.length,
      nsirsAtribuidas,
      servicosPendentesNaFila,
      itens,
      ...aggregateFilaMetrics(itens, { nsirsAtribuidas, servicosPendentesNaFila }),
    };
  }

  const fila = data.minhaFila && typeof data.minhaFila === 'object' ? data.minhaFila : data;
  const itens = Array.isArray(fila.itens) ? fila.itens : [];
  const nsirsAtribuidas = fila.nsirsAtribuidas ?? fila.nsirsAtivas ?? itens.length;
  const servicosPendentesNaFila = fila.servicosPendentesNaFila ?? countPendentesFila(itens);

  return {
    nsirsAtivas: fila.nsirsAtivas ?? itens.length,
    nsirsAtribuidas,
    servicosPendentesNaFila,
    itens,
    ...aggregateFilaMetrics(itens, {
      nsirsAtribuidas,
      nsirsAtivas: fila.nsirsAtivas,
      servicosPendentesNaFila,
    }),
  };
};

/** Resolve escopo digitador: self (JWT) ou alvo do bridge admin. */
export const resolveDigitadorScopeId = (authData, targetDigitadorId) =>
  targetDigitadorId || authData?.id || null;

/** Body da fila: digitador + geo/código — sem período */
export const normalizeMinhaFilaFilterForApi = (filter, authData, targetDigitadorId) => {
  const digitadorId = resolveDigitadorScopeId(authData, targetDigitadorId);
  const body = normalizeFilterForApi({
    ...filter,
    digitadorId: digitadorId ? [digitadorId] : [],
  });

  delete body.inicio;
  delete body.fim;
  delete body.dateTypes;

  return body;
};

export const formatPeriodoCurto = (inicio, fim) => {
  if (inicio == null || fim == null) return '';
  return `${moment(inicio).format('DD/MM')} – ${moment(fim).format('DD/MM')}`;
};

/** Label do chip — medição atual/última incluem intervalo DD/MM – DD/MM. */
export const getPeriodoPresetLabel = (presetId, ref = moment()) => {
  const preset = PERIODO_PRESETS.find((p) => p.id === presetId);
  if (!preset) return '';

  if (
    presetId === PERIODO_PRESET_KEYS.MEDICAO_ATUAL
    || presetId === PERIODO_PRESET_KEYS.ULTIMA_MEDICAO
  ) {
    const { inicio, fim } = getPeriodoPresetRange(presetId, ref);
    const curto = formatPeriodoCurto(inicio, fim);
    return curto ? `${preset.label} · ${curto}` : preset.label;
  }

  return preset.label;
};

export const getPrimeiroNome = (nome) => {
  if (!nome || typeof nome !== 'string') return '';
  return nome.trim().split(/\s+/)[0];
};

export const buildLocalLabel = (item) => {
  if (!item) return '';
  if (item.localLabel) return item.localLabel;
  return [item.poloNome, item.subestacaoNome, item.alimentadorNome]
    .filter(Boolean)
    .join(' / ');
};

/** CTA da fila / drawer: Ver | Continuar (EM_DIGITACAO) | Iniciar */
export const getFilaItemCta = (item, { readOnly = false } = {}) => {
  if (readOnly) {
    return { label: 'Ver', variant: 'outlined' };
  }
  const digitados = item?.digitados ?? 0;
  const total = item?.totalServicos ?? item?.total ?? 0;
  if (total > 0 && digitados >= total) {
    return { label: 'Ver', variant: 'outlined' };
  }
  if (item?.status === EM_DIGITACAO) {
    return { label: 'Continuar', variant: 'contained' };
  }
  return { label: 'Iniciar', variant: 'outlined' };
};

export const normalizeOperadorFilterForApi = (filter, authData, targetDigitadorId) => {
  const digitadorId = resolveDigitadorScopeId(authData, targetDigitadorId);
  return normalizeFilterForApi({
    ...filter,
    digitadorId: digitadorId ? [digitadorId] : [],
    dateTypes: ['DATA_DIGITACAO'],
  });
};

export const TENDENCIA_COPY = {
  acima: '↑ acima da sua média',
  abaixo: '↓ abaixo da sua média',
  na_media: '→ na sua média',
};

/** Filtros rápidos da subtable de serviços (histórico por NSIR). */
export const HISTORICO_NSMP_FILTERS = {
  TODOS: 'TODOS',
  PENDENTES: 'PENDENTES',
  NO_PERIODO: 'NO_PERIODO',
  FORA_PERIODO: 'FORA_PERIODO',
};

export const HISTORICO_NSMP_FILTER_OPTIONS = [
  { id: HISTORICO_NSMP_FILTERS.TODOS, label: 'Todos' },
  { id: HISTORICO_NSMP_FILTERS.PENDENTES, label: 'Pendentes' },
  { id: HISTORICO_NSMP_FILTERS.NO_PERIODO, label: 'No período' },
  { id: HISTORICO_NSMP_FILTERS.FORA_PERIODO, label: 'Fora' },
];

/** Ordenação do histórico (menu Widget + body API `sortBy`/`sortDir`). */
export const HISTORICO_SORT_MODES = {
  PENDENTES_FIRST: 'PENDENTES_FIRST',
  DATA_DESC: 'DATA_DESC',
  DATA_ASC: 'DATA_ASC',
  CODIGO_ASC: 'CODIGO_ASC',
  CODIGO_DESC: 'CODIGO_DESC',
  STATUS: 'STATUS',
};

export const HISTORICO_SORT_OPTIONS = [
  { mode: HISTORICO_SORT_MODES.PENDENTES_FIRST, title: 'Pendentes primeiro' },
  { mode: HISTORICO_SORT_MODES.DATA_DESC, title: 'Data digitação ↓' },
  { mode: HISTORICO_SORT_MODES.DATA_ASC, title: 'Data digitação ↑' },
  { mode: HISTORICO_SORT_MODES.STATUS, title: 'Status NSIR' },
  { mode: HISTORICO_SORT_MODES.CODIGO_ASC, title: 'Código A–Z' },
  { mode: HISTORICO_SORT_MODES.CODIGO_DESC, title: 'Código Z–A' },
];

export const FILA_SORT_OPTIONS = [
  { mode: FILA_SORT_MODES.STATUS, title: 'Status (padrão)' },
  { mode: FILA_SORT_MODES.VENCIMENTO_ASC, title: 'Vencimento ↑' },
  { mode: FILA_SORT_MODES.VENCIMENTO_DESC, title: 'Vencimento ↓' },
  { mode: FILA_SORT_MODES.CODIGO_ASC, title: 'Código A–Z' },
  { mode: FILA_SORT_MODES.CODIGO_DESC, title: 'Código Z–A' },
];

/** Campos enviados em POST /me/historico para ordenação server-side. */
export const historicoSortToApi = (mode = HISTORICO_SORT_MODES.PENDENTES_FIRST) => {
  switch (mode) {
    case HISTORICO_SORT_MODES.DATA_ASC:
      return { sortBy: 'dataDigitacao', sortDir: 'ASC' };
    case HISTORICO_SORT_MODES.DATA_DESC:
      return { sortBy: 'dataDigitacao', sortDir: 'DESC' };
    case HISTORICO_SORT_MODES.CODIGO_ASC:
      return { sortBy: 'nsirCodigo', sortDir: 'ASC' };
    case HISTORICO_SORT_MODES.CODIGO_DESC:
      return { sortBy: 'nsirCodigo', sortDir: 'DESC' };
    case HISTORICO_SORT_MODES.STATUS:
      return { sortBy: 'nsirStatus', sortDir: 'ASC' };
    case HISTORICO_SORT_MODES.PENDENTES_FIRST:
    default:
      return { sortBy: 'pendentesNsir', sortDir: 'DESC' };
  }
};

const historicoRowStatus = (row) => row.nsirStatus ?? row.status ?? '';
const historicoRowCodigo = (row) => String(row.nsirCodigo ?? '');
const historicoRowData = (row) => {
  const ts = Number(row.dataDigitacao);
  return Number.isNaN(ts) ? 0 : ts;
};
const historicoRowPendentes = (row) => {
  const n = row.pendentesNsir ?? row.pendentes ?? 0;
  return Number(n) || 0;
};

/**
 * Ordena a página retornada (fallback se o BE ainda ignorar sortBy).
 * Com paginação server-side, a ordenação global correta depende do servidor.
 */
export const sortHistoricoRows = (rows, mode = HISTORICO_SORT_MODES.PENDENTES_FIRST) => {
  const list = [...(rows || [])];
  const byCodigo = (a, b) =>
    historicoRowCodigo(a).localeCompare(historicoRowCodigo(b), undefined, { numeric: true });

  if (mode === HISTORICO_SORT_MODES.CODIGO_ASC) {
    return list.sort(byCodigo);
  }
  if (mode === HISTORICO_SORT_MODES.CODIGO_DESC) {
    return list.sort((a, b) => byCodigo(b, a));
  }
  if (mode === HISTORICO_SORT_MODES.DATA_ASC) {
    return list.sort((a, b) => {
      const byData = historicoRowData(a) - historicoRowData(b);
      return byData !== 0 ? byData : byCodigo(a, b);
    });
  }
  if (mode === HISTORICO_SORT_MODES.DATA_DESC) {
    return list.sort((a, b) => {
      const byData = historicoRowData(b) - historicoRowData(a);
      return byData !== 0 ? byData : byCodigo(a, b);
    });
  }
  if (mode === HISTORICO_SORT_MODES.STATUS) {
    return list.sort((a, b) => {
      const byStatus = statusSortRank(historicoRowStatus(a)) - statusSortRank(historicoRowStatus(b));
      if (byStatus !== 0) return byStatus;
      const byPend = historicoRowPendentes(b) - historicoRowPendentes(a);
      return byPend !== 0 ? byPend : byCodigo(a, b);
    });
  }
  // PENDENTES_FIRST (padrão)
  return list.sort((a, b) => {
    const byPend = historicoRowPendentes(b) - historicoRowPendentes(a);
    if (byPend !== 0) return byPend;
    const byData = historicoRowData(b) - historicoRowData(a);
    return byData !== 0 ? byData : byCodigo(a, b);
  });
};

/** Verifica se dataDigitacao (epoch) está dentro do intervalo inicio/fim. */
export const isDataNoPeriodo = (inicio, fim, dataDigitacao) => {
  if (dataDigitacao == null || inicio == null || fim == null) return false;
  const ts = Number(dataDigitacao);
  if (Number.isNaN(ts) || ts <= 0) return false;
  return ts >= inicio && ts <= fim;
};

export const formatHistoricoDiaFilter = (dataDigitacaoFilter) => {
  if (dataDigitacaoFilter == null) return '';
  const parsed = moment(Number(dataDigitacaoFilter) > 0 ? Number(dataDigitacaoFilter) : dataDigitacaoFilter);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
};

export const getHistoricoVolumeUnit = (lente) => (
  lente?.unidade === 'postes' || lente?.id === 'USO_MUTUO' ? 'postes' : 'servicos'
);

/** Normaliza métricas da linha NSIR (novos campos + fallback legado). */
export const getHistoricoNsirMetrics = (row, volumeUnit = 'servicos') => {
  const isPostes = volumeUnit === 'postes';
  const totalServicosNsir = row.totalServicosNsir ?? row.totalServicos ?? row.nsmpCount ?? 0;
  const pendentesNsir = row.pendentesNsir ?? row.pendentes ?? 0;
  const totalServicosUsuario = isPostes
    ? (row.postesDigitados ?? row.totalServicosUsuario ?? row.totalDigitados ?? 0)
    : (row.totalServicosUsuario ?? row.totalDigitados ?? 0);
  const pendentesUsuario = row.pendentesUsuario ?? 0;
  const digitadosForaPeriodo = row.digitadosForaPeriodo ?? 0;
  return {
    totalServicosNsir,
    pendentesNsir,
    totalServicosUsuario,
    pendentesUsuario,
    digitadosForaPeriodo,
    volumeUnit,
  };
};
